import React, {useCallback, useEffect, useState} from 'react';

// styles
import {
  InputLabel,
  Content,
  FormContent,
  StyledButton,
} from '../LoginPage-styles';
import {Subtitle, TitleExtraLarge} from '../../../shared/styles';

// services
import {ConfigManager} from '../../../shared/services/ConfigManager';
import {CreditBookApi} from '../../../shared/services/ApiService';

// components
import {FormControl, FormHelperText, Input} from '@mui/joy';

// hooks
import {useMutation} from '@tanstack/react-query';

// types
import {Auth} from '../../../shared/services/ApiService/types/endpoints';

// helpers
import {validateUsername} from '../../../shared/services/auth';
import {useTranslation} from 'react-i18next';
import {toast} from 'react-toastify';

// styles
import {TitleContent} from '../LoginPage-styles';

interface UsernameFormProps {
  onSubmit: (username: string) => void;
  handleOnPending: (isPending: boolean) => void;
}

const UsernameForm = (props: UsernameFormProps) => {
  const {t} = useTranslation();
  const {onSubmit, handleOnPending} = props;

  const portalConfig = ConfigManager.getConfig();

  const [username, setUsername] = useState('');
  const [errorMessage, setErrorMessage] = useState('');

  /**
   * Handle username change
   */
  const handleUsernameChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    setUsername(value);
    setErrorMessage('');
  };

  const {isPending, mutate: auth} = useMutation({
    mutationFn: (name: string) => {
      return CreditBookApi.auth.authenticate(name);
    },
    onSuccess: (data: Auth, variables: string) => {
      toast.success(data.data.message, {
        autoClose: 10000,
      });
      onSubmit(variables);
    },
    onError: () => {
      toast.error(t('global_errorOccurred'));
    },
  });

  /**
   * Handle submit and set username
   */
  const handleSubmit = useCallback(
    (event: React.FormEvent<HTMLFormElement>) => {
      event.preventDefault();

      if (!validateUsername(username)) {
        setErrorMessage(t('login_username_input_error'));
        return;
      }

      auth(username);
    },
    [auth, username, t],
  );

  // Update the isPending state
  useEffect(() => {
    handleOnPending(isPending);
  }, [handleOnPending, isPending]);

  return (
    <form onSubmit={handleSubmit}>
      <Content>
        <FormContent>
          <TitleContent>
            <TitleExtraLarge>
              {t('login_title', {lender: portalConfig?.lenderName})}
            </TitleExtraLarge>
            <Subtitle>{t('login_message')}</Subtitle>
          </TitleContent>
          <FormControl error={!!errorMessage}>
            <InputLabel bold="bold">{t('login_username_input')}</InputLabel>
            <Input
              type="text"
              value={username}
              onChange={handleUsernameChange}
              placeholder={t('login_username_input_placeholder')}
              autoFocus
            />
            <FormHelperText>{errorMessage}</FormHelperText>
          </FormControl>
        </FormContent>
        <StyledButton
          type="submit"
          size="lg"
          fullWidth
          disabled={username.trim().length < 1}
          loading={isPending}>
          {t('login_request_code_button')}
        </StyledButton>
      </Content>
    </form>
  );
};

export default UsernameForm;
